import type { TargetedPointerEvent } from 'preact';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { AppIcon } from './AppIcon';

type Control = 'grip' | 'up' | 'down';

/** Reorders whole sections in a draft list; nothing changes until Save. */
export function SectionReorderDialog({ names, index, onSave, onClose }: { names: string[]; index: number; onSave: (order: number[]) => void; onClose: () => void }) {
  const id = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const list = useRef<HTMLOListElement>(null);
  const [order, setOrder] = useState(() => names.map((_, i) => i));
  const [dragging, setDragging] = useState<{ section: number; pointerId: number } | null>(null);
  const [message, setMessage] = useState('');
  const refocus = useRef<{ section: number; control: Control } | null>({ section: index, control: 'grip' });

  useEffect(() => { dialog.current?.showModal(); }, []);
  // Moving a keyed row can blur its control; keep focus with the section that moved.
  useLayoutEffect(() => {
    const target = refocus.current; refocus.current = null;
    if (!target) return;
    const row = list.current?.querySelector(`[data-section="${target.section}"]`);
    // A chevron disabled at the end of the list hands focus to the opposite chevron.
    const fallback = target.control === 'up' ? 'down' : target.control === 'down' ? 'up' : 'grip';
    const control = [target.control, fallback, 'grip'].map(name => row?.querySelector<HTMLButtonElement>(`[data-control="${name}"]:not(:disabled)`)).find(Boolean);
    control?.focus({ preventScroll: true });
  }, [order]);

  const move = (section: number, to: number, control?: Control) => {
    const from = order.indexOf(section);
    if (to < 0 || to >= order.length || to === from) return;
    const next = order.filter(s => s !== section); next.splice(to, 0, section);
    if (control) refocus.current = { section, control };
    setOrder(next);
    setMessage(`Moved ${names[section]} to position ${to + 1} of ${order.length}.`);
  };
  const start = (event: TargetedPointerEvent<HTMLButtonElement>, section: number) => {
    if (!event.isPrimary || event.button !== 0 || dragging) return;
    event.preventDefault(); event.currentTarget.focus(); event.currentTarget.setPointerCapture(event.pointerId);
    setDragging({ section, pointerId: event.pointerId });
  };
  const drag = (event: TargetedPointerEvent<HTMLButtonElement>) => {
    if (dragging?.pointerId !== event.pointerId) return;
    const rows = [...(list.current?.children ?? [])] as HTMLElement[];
    const over = rows.findIndex(row => event.clientY < row.getBoundingClientRect().bottom);
    move(dragging.section, over < 0 ? rows.length - 1 : over, 'grip');
  };
  const stop = () => setDragging(null);
  // Clicks on the dimmed backdrop target the dialog itself; only one that starts and ends there cancels.
  const outside = (event: MouseEvent) => {
    const box = dialog.current!.getBoundingClientRect();
    return event.target === dialog.current && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom);
  };
  const pressedOutside = useRef(false);

  return <dialog class="section-reorder-dialog" ref={dialog} aria-labelledby={`${id}-title`} onCancel={event => { event.preventDefault(); onClose(); }}
    onPointerDown={event => { pressedOutside.current = outside(event); }} onClick={event => { if (pressedOutside.current && outside(event)) onClose(); pressedOutside.current = false; }} onKeyDown={event => {
    if (event.key !== 'Tab') return; const buttons = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
    if (event.shiftKey && document.activeElement === buttons[0]) { event.preventDefault(); buttons.at(-1)?.focus(); }
    else if (!event.shiftKey && document.activeElement === buttons.at(-1)) { event.preventDefault(); buttons[0]?.focus(); }
  }}>
    <h2 id={`${id}-title`}>Reorder sections</h2>
    <p class="sr-only" id={`${id}-help`}>Drag, or use the up and down arrow keys, to move this section.</p>
    <ol class="section-reorder-list" ref={list}>{order.map((section, at) => <li key={section} data-section={section} class={dragging?.section === section ? 'dragging' : undefined}>
      <button type="button" class="icon section-reorder-grip" data-control="grip" aria-label={`Reorder ${names[section]}`} aria-describedby={`${id}-help`}
        onPointerDown={event => start(event, section)} onPointerMove={drag} onPointerUp={stop} onPointerCancel={stop} onLostPointerCapture={stop}
        onKeyDown={event => { if (event.key === 'ArrowUp' || event.key === 'ArrowDown') { event.preventDefault(); move(section, at + (event.key === 'ArrowUp' ? -1 : 1), 'grip'); } }}>
        <svg width="12" height="20" viewBox="0 0 12 20" aria-hidden="true" fill="currentColor">{[3, 9].flatMap(x => [4, 10, 16].map(y => <circle cx={x} cy={y} r="1.6" />))}</svg>
      </button>
      <span class="section-reorder-name"><strong>{names[section]}</strong><span>Section {at + 1} of {order.length}</span></span>
      <button type="button" class="icon" data-control="up" aria-label={`Move ${names[section]} up`} disabled={at === 0} onClick={() => move(section, at - 1, 'up')}><AppIcon name="chevron-up" /></button>
      <button type="button" class="icon" data-control="down" aria-label={`Move ${names[section]} down`} disabled={at === order.length - 1} onClick={() => move(section, at + 1, 'down')}><AppIcon name="chevron-down" /></button>
    </li>)}</ol>
    <p class="sr-only" role="status" aria-live="polite">{message}</p>
    <div class="row section-reorder-buttons"><button type="button" class="secondary" onClick={onClose}>Cancel</button><button type="button" onClick={() => onSave(order)}>Save</button></div>
  </dialog>;
}
